import React, { useState, useRef, useCallback, useMemo, memo, useEffect } from "react";
import Popper from '@material-ui/core/Popper';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import appConstants from "../../../../appConstants";
import Tooltip from "../../../common/StyledToolTip";

const findLockStateByValue = (value) => 
    Object.values(appConstants.routeLockStates).find(
        routeLockState => routeLockState.value === value
    );

const POPPER_MODIFIERS = {
    flip: {
        enabled: true,
    },
    preventOverflow: {
        enabled: true,
        boundariesElement: 'scrollParent',
    }
};

const LOCK_STATES = Object.values(appConstants.routeLockStates);

const styles = theme => ({
    lockStatusIconStyles: {
        padding: "6px 0px",
        cursor: "pointer",
        display: 'inline-block',
        '&:not(:last-child)':{
            '&>$popperIcon': {
                borderRight: `1px solid ${theme.AppBGColor}`,
            },
        },
        '&:hover>$popperIcon': {
            color: theme.HeaderSelectedText,
        },
        '&:hover>$lockStateText': {
            color: theme.HeaderSelectedText,
        },
    },
    paper: {
        backgroundColor: theme.ModalBGColor,
    },
    lockStateText: {
        color: theme.TextColor,
        fontSize: theme.textFontSize,
        padding: '0px 12px',
    },
    selectedLockState: {
        '&>$popperIcon': {
            color: theme.HeaderSelectedText,
        },
        '&>$lockStateText': {
            color: theme.HeaderSelectedText,
        },
    },
    root: {
        height: '100%',
        width: '100%',
        display: 'table',
    },
    icon: {
        fontSize: '16px',
        textAlign: 'center',
        display: 'table-cell',
        verticalAlign: 'middle',
        color: theme.iconColor,
        cursor: 'pointer',
    },
    popperIcon: {
        fontSize: '16px',
        textAlign: 'center',
        display: 'block',
        color: theme.iconColor,
        cursor: 'pointer',
    },
    iconLock: {
        color: theme.HeaderSelectedText,
    },
    popper: {
        zIndex: 1,
        '&[x-placement*="right"] $arrow': {
            left: 0,
            marginLeft: '-0.9em',
            height: '3em',
            width: '1em',
            '&::before': {
                borderWidth: '1em 1em 1em 0',
                borderColor: `transparent ${theme.ModalBGColor} transparent transparent`,
            },
        },
    },
    arrow: {
        position: 'absolute',
        fontSize: 7,
        width: '3em',
        height: '3em',
        '&::before': {
            content: '""',
            margin: 'auto',
            display: 'block',
            width: 0,
            height: 0,
            borderStyle: 'solid',
        },
    },
});

const LockStatusRenderer = (props) => {
    const { classes, value, colDef, node, data, eGridCell } = props;
    const arrowRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [lockState, setLockState] = useState(findLockStateByValue(value));

    useEffect(() => {
        setLockState(findLockStateByValue(value));
    }, [value]);
    
    const portalContainer = useMemo(() => {
        if (eGridCell?.parentElement?.parentElement) {
            return eGridCell.parentElement.parentElement;
        }
        return document.body;
    }, [eGridCell]);
    
    const handleClick = useCallback((event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    }, [anchorEl]);
    
    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);
    
    const onLockStatusChanged = useCallback((event, lockStatus) => {
        const { field } = colDef || {};
        if (node?.setDataValue) {
            node.setDataValue(field, lockStatus);
        }
        setAnchorEl(null);
        setLockState(findLockStateByValue(lockStatus));
        event.stopPropagation();
    }, [colDef, node]);
    
    const getLockStatusClasses = useCallback((routeLockState) => {
        let className = classes.lockStatusIconStyles;
        if (routeLockState.value === lockState.value) {
            className += ` ${classes.selectedLockState}`;
        }
        return className;
    }, [classes.lockStatusIconStyles, classes.selectedLockState, lockState]);
    
    const iconClasses = useMemo(() => {
        let className = `${lockState.icon} ${classes.icon}`;
        if (lockState.text !== "Unlock") {
            className += ` ${classes.iconLock}`;
        }
        return className;
    }, [lockState, classes.icon, classes.iconLock]);
    
    const popperModifiers = useMemo(() => ({
        ...POPPER_MODIFIERS,
        arrow: {
            enabled: true,
            element: arrowRef.current,
        }
    }), [arrowRef.current]);
    
    // If it's an unrouted entry or no lock state, don't show anything
    if ((data && data.routeName === "UNRTD") || !lockState) {
        return null;
    }
    
    return (
        <div className={classes.root}>
            <Tooltip title={lockState.toolTipInfo} enterDelay={500}>
                <i 
                    className={iconClasses}
                    onClick={handleClick}
                />
            </Tooltip>
            <Popper
                id={anchorEl ? 'lock-status-popper' : null}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="right"
                className={classes.popper}
                modifiers={popperModifiers}
                container={portalContainer}
            >
                <ClickAwayListener onClickAway={handleClose} onScroll={handleClose}>
                    <Paper className={classes.paper}>
                        <span className={classes.arrow} ref={arrowRef} />
                        {LOCK_STATES.map((routeLockState) => (
                            <div 
                                className={getLockStatusClasses(routeLockState)}
                                onClick={event => onLockStatusChanged(event, routeLockState.value)}
                                key={routeLockState.text}
                            >
                                <i className={`${routeLockState.icon} ${classes.popperIcon}`} />
                                <div className={classes.lockStateText}>{routeLockState.text}</div>
                            </div>
                        ))}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </div>
    );
};

const MemoizedLockStatusRenderer = memo(LockStatusRenderer);
export default withStyles(styles)(MemoizedLockStatusRenderer);